import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { GET_CAMPAIGN } from "../graphql/campaigns";
import { buildImageUrl } from "../config";

const CampaignShowPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery(GET_CAMPAIGN, {
    variables: { id },
  });

  const campaign = data?.campaign;

  if (loading)
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return <Alert severity="error">Failed to load campaign details.</Alert>;

  if (!campaign) {
    return (
      <Alert severity="warning">Campaign not found or has been removed.</Alert>
    );
  }

  return (
    <Box sx={{ py: 10, backgroundColor: "#F5F5F5" }}>
      <Container maxWidth="lg">
        {/* Title */}
        <Typography
          sx={{
            fontFamily: "Playfair Display, serif",
            fontSize: { xs: 32, md: 42 },
            textAlign: "center",
            mb: 4,
            textTransform: "uppercase",
          }}
        >
          {campaign.name}
        </Typography>

        {/* All Images */}
        <Grid container spacing={{ xs: 1.5, md: 3 }}>
          {campaign.images.map((src: string, idx: number) => (
            <Grid item xs={6} sm={4} md={4} key={idx}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "100%",
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: "#f6f6f6",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
                }}
              >
                <Box
                  component="img"
                  loading="lazy"
                  decoding="async"
                  src={buildImageUrl(src)}
                  alt={`Campaign image ${idx + 1}`}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    objectPosition: "center",
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default CampaignShowPage;
